/**
 * Wire adapter: stage-runtime ActivationFrames <-> Codec latent-frame
 * "activation" profile (@codecai/web v0.6+).
 *
 * This is the frozen wire contract for stage traffic (Trystero `sf` action in
 * Phase B/C): one msgpack header per stream, then one msgpack frame per
 * prefill chunk / decode token, `raw` pipeline, fp32 or fp16 payload with
 * posStart / tokens / stageIndex sideband. See Codec spec/PIPELINES.md
 * § Activation profile.
 */
import { ActivationStreamEncoder, ActivationStreamDecoder, decodeLatentHeaderMsgpack, decodeLatentFrameMsgpack, } from '@codecai/web';
const DTYPE_TO_CODEC = { f32: 'fp32', f16: 'fp16' };
export function activationSpaceId(modelId) {
    return `activation:${modelId}`;
}
export function createActivationWireEncoder(opts) {
    if (!(opts.nEmbd > 0) || !Number.isInteger(opts.nEmbd)) {
        throw new RangeError(`nEmbd must be a positive integer, got ${opts.nEmbd}`);
    }
    const encoder = new ActivationStreamEncoder({
        latentSpaceId: activationSpaceId(opts.modelId),
        nEmbd: opts.nEmbd,
        dtype: DTYPE_TO_CODEC[opts.dtype],
        pipeline: 'raw',
    });
    return {
        headerBytes: () => encoder.header(),
        encodeFrame: (activations, meta) => {
            if (activations.length % opts.nEmbd !== 0) {
                throw new RangeError(`activations length ${activations.length} is not a multiple of nEmbd ${opts.nEmbd}`);
            }
            return encoder.frame(activations, {
                seq: meta.seq,
                keyframe: true,
                done: meta.done ?? false,
                finishReason: meta.finishReason,
                posStart: meta.posStart,
                tokens: meta.tokens,
                stageIndex: opts.stageIndex,
            });
        },
    };
}
const CODEC_TO_DTYPE = { fp32: 'f32', fp16: 'f16' };
export function createActivationWireDecoder(headerBytes) {
    const header = decodeLatentHeaderMsgpack(headerBytes);
    if (header.profile !== 'activation') {
        throw new Error(`expected an activation-profile latent stream header, got profile=${header.profile ?? 'latent'}`);
    }
    const dtype = CODEC_TO_DTYPE[header.dtype];
    if (!dtype) {
        throw new Error(`unsupported activation wire dtype: ${header.dtype}`);
    }
    const spaceId = header.latent_space_id;
    const modelId = spaceId.startsWith('activation:') ? spaceId.slice('activation:'.length) : spaceId;
    const decoder = new ActivationStreamDecoder(header);
    return {
        modelId,
        nEmbd: header.nEmbd,
        dtype,
        decodeFrameBytes: (bytes) => {
            const frame = decodeLatentFrameMsgpack(bytes);
            const data = decoder.decodeFrame(frame);
            return {
                activations: data.activations,
                tokenCount: data.tokenCount,
                seq: frame.seq,
                done: frame.done,
                posStart: data.posStart,
                tokens: data.tokens,
                stageIndex: data.stageIndex,
                finishReason: frame.finish_reason,
            };
        },
    };
}
